import React, { createContext, useContext } from 'react';
import type { Theme } from '../hooks/useIdentityPreferences';

export interface IdentityPreferencesContextValue {
  preferences: Record<string, any>;
  theme: Theme;
  isLoading: boolean;
  error: string | null;
  updatePreferences: (updates: Record<string, any>) => Promise<boolean>;
  setTheme: (theme: Theme) => Promise<boolean>;
}

const IdentityPreferencesContext =
  createContext<IdentityPreferencesContextValue | null>(null);

/**
 * Provider for identity preferences.
 * Center PWA provides a Redux+API-backed implementation.
 * Other apps can provide a simpler localStorage-only implementation.
 */
export function IdentityPreferencesProvider({
  value,
  children,
}: Readonly<{
  value: IdentityPreferencesContextValue;
  children: React.ReactNode;
}>) {
  return (
    <IdentityPreferencesContext.Provider value={value}>
      {children}
    </IdentityPreferencesContext.Provider>
  );
}

export function useIdentityPreferencesContext(): IdentityPreferencesContextValue {
  const ctx = useContext(IdentityPreferencesContext);
  if (!ctx) {
    // Return a safe default so the component works without the provider
    return {
      preferences: {},
      theme: getLocalStorageTheme(),
      isLoading: false,
      error: null,
      updatePreferences: async () => false,
      setTheme: async (t: Theme) => {
        if (globalThis.window !== undefined) {
          localStorage.setItem('center-app-theme-preference', t);
        }
        return true;
      },
    };
  }
  return ctx;
}

function getLocalStorageTheme(): Theme {
  if (globalThis.window !== undefined) {
    const stored = localStorage.getItem('center-app-theme-preference');
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as Theme;
    }
  }
  return 'system';
}
